import React, { forwardRef, useEffect } from 'react';
import { Input, Typography } from '@ghq-abi/design-system-v2';

import { cn } from '~/lib/utils';

import { SEARCH_BAR_STYLES } from '../constants';

import { ClearButton } from './ClearButton';
import { SearchIcon } from './SearchIcon';

interface SearchInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  onClear: () => void;
  placeholder?: string;
  disabled?: boolean;
  showClearButton?: boolean;
  isFocused: boolean;
  autoFocus?: boolean;
  className?: string;
}

export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      value,
      onChange,
      onKeyDown,
      onFocus,
      onBlur,
      onClear,
      placeholder,
      disabled = false,
      showClearButton = true,
      isFocused,
      autoFocus = false,
      className
    },
    ref
  ) => {
    const hasValue = value.length > 0;

    // Auto focus effect
    useEffect(() => {
      if (autoFocus && ref && 'current' in ref && ref.current) {
        ref.current.focus();
      }
    }, [autoFocus, ref]);

    return (
      <div className={cn(SEARCH_BAR_STYLES.INPUT_CONTAINER, className)}>
        <div className="relative w-full h-full">
          <SearchIcon isFocused={isFocused} disabled={disabled} />

          <Input
            ref={ref}
            type="text"
            value={value}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            disabled={disabled}
            className={cn(
              SEARCH_BAR_STYLES.INPUT,
              'pl-10 pr-12 text-sm font-medium',
              disabled && SEARCH_BAR_STYLES.INPUT_DISABLED
            )}
          />

          {/* Placeholder customizado com Typography */}
          {!value && placeholder && (
            <div className="absolute left-10 top-1/2 transform -translate-y-1/2 pointer-events-none">
              <Typography variant="body-sm-medium" color="light">
                {placeholder}
              </Typography>
            </div>
          )}

          {showClearButton && hasValue && !disabled && (
            <ClearButton onClear={onClear} disabled={disabled} />
          )}
        </div>
      </div>
    );
  }
);

SearchInput.displayName = 'SearchInput';
